import React, { useState, useEffect } from 'react';
import { ClockIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline';
import { reservationApi } from '../../services/reservationApi';
import { ReservationCreate } from '../../types/reservation';
import { Seat } from '../../types';

interface ReservationFormProps {
  selectedDate: Date;
  selectedSeat?: Seat | null;
  onReservationComplete: () => void;
  onCancel: () => void;
}

interface TimeSlot {
  start: string;
  end: string;
  duration: number;
  label: string;
}

const TIME_SLOTS: TimeSlot[] = [
  { start: '09:00', end: '11:00', duration: 2, label: '2시간 (09:00-11:00)' },
  { start: '09:00', end: '13:00', duration: 4, label: '4시간 (09:00-13:00)' },
  { start: '09:00', end: '18:00', duration: 9, label: '9시간 (09:00-18:00)' },
  { start: '13:00', end: '15:00', duration: 2, label: '2시간 (13:00-15:00)' },
  { start: '13:00', end: '17:00', duration: 4, label: '4시간 (13:00-17:00)' },
  { start: '13:00', end: '22:00', duration: 9, label: '9시간 (13:00-22:00)' },
  { start: '18:00', end: '20:00', duration: 2, label: '2시간 (18:00-20:00)' },
  { start: '18:00', end: '22:00', duration: 4, label: '4시간 (18:00-22:00)' },
];

export const ReservationForm: React.FC<ReservationFormProps> = ({
  selectedDate,
  selectedSeat,
  onReservationComplete,
  onCancel,
}) => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null
  );
  const [customStartTime, setCustomStartTime] = useState('09:00');
  const [customEndTime, setCustomEndTime] = useState('11:00');
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictCheck, setConflictCheck] = useState<{
    hasConflict: boolean;
    message?: string;
  } | null>(null);

  // 날짜와 시간을 조합하여 ISO 문자열 생성
  const combineDateTime = (date: Date, time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const dateTime = new Date(date);
    dateTime.setHours(hours, minutes, 0, 0);
    return dateTime.toISOString();
  };

  // 충돌 검사
  const checkConflict = async (startTime: string, endTime: string) => {
    if (!selectedSeat) return;

    try {
      const startDateTime = combineDateTime(selectedDate, startTime);
      const endDateTime = combineDateTime(selectedDate, endTime);

      const conflict = await reservationApi.checkReservationConflict(
        selectedSeat.id,
        startDateTime,
        endDateTime
      );

      setConflictCheck({
        hasConflict: conflict.has_conflict,
        message: conflict.has_conflict
          ? '선택한 시간에 다른 예약이 있습니다. 다른 시간을 선택해주세요.'
          : undefined,
      });
    } catch (error) {
      console.error('충돌 검사 실패:', error);
    }
  };

  // 시간 슬롯 선택 시 충돌 검사
  useEffect(() => {
    if (selectedTimeSlot && selectedSeat) {
      checkConflict(selectedTimeSlot.start, selectedTimeSlot.end);
    }
  }, [selectedTimeSlot, selectedSeat, selectedDate]);

  // 커스텀 시간 변경 시 충돌 검사
  useEffect(() => {
    if (useCustomTime && selectedSeat && customStartTime && customEndTime) {
      checkConflict(customStartTime, customEndTime);
    }
  }, [
    useCustomTime,
    customStartTime,
    customEndTime,
    selectedSeat,
    selectedDate,
  ]);

  // 예약 생성
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSeat) {
      setError('좌석을 선택해주세요.');
      return;
    }

    if (!selectedTimeSlot && !useCustomTime) {
      setError('시간을 선택해주세요.');
      return;
    }

    if (conflictCheck?.hasConflict) {
      setError('선택한 시간에 다른 예약이 있습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let startTime: string, endTime: string, duration: number;

      if (useCustomTime) {
        startTime = customStartTime;
        endTime = customEndTime;

        // 시간 차이 계산
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        duration = Math.round((endMinutes - startMinutes) / 60);

        if (duration <= 0) {
          throw new Error('종료 시간은 시작 시간보다 늦어야 합니다.');
        }
      } else if (selectedTimeSlot) {
        startTime = selectedTimeSlot.start;
        endTime = selectedTimeSlot.end;
        duration = selectedTimeSlot.duration;
      } else {
        throw new Error('시간을 선택해주세요.');
      }

      const reservationData: ReservationCreate = {
        seat_id: selectedSeat.id,
        start_time: combineDateTime(selectedDate, startTime),
        end_time: combineDateTime(selectedDate, endTime),
        duration_hours: duration,
      };

      // 임시 사용자 ID (실제로는 로그인한 사용자 ID 사용)
      const userId = 'temp-user-id';

      await reservationApi.createReservation(reservationData, userId);

      onReservationComplete();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : '예약 생성에 실패했습니다.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">좌석 예약</h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 선택된 정보 표시 */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center text-sm">
            <MapPinIcon className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-gray-600">좌석:</span>
            <span className="ml-2 font-medium">
              {selectedSeat
                ? `${selectedSeat.seat_number}번 좌석`
                : '선택되지 않음'}
            </span>
          </div>

          <div className="flex items-center text-sm">
            <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-gray-600">날짜:</span>
            <span className="ml-2 font-medium">
              {selectedDate.toLocaleDateString('ko-KR')}
            </span>
          </div>
        </div>

        {/* 시간 선택 방식 */}
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={!useCustomTime}
                onChange={() => setUseCustomTime(false)}
                className="mr-2"
              />
              <span>정해진 시간 선택</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={useCustomTime}
                onChange={() => setUseCustomTime(true)}
                className="mr-2"
              />
              <span>직접 시간 입력</span>
            </label>
          </div>

          {/* 정해진 시간 슬롯 선택 */}
          {!useCustomTime && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                이용 시간 선택
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TIME_SLOTS.map((slot, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedTimeSlot(slot)}
                    className={`
                      p-3 text-left border rounded-lg transition-colors
                      ${
                        selectedTimeSlot === slot
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="font-medium">{slot.duration}시간</div>
                    <div className="text-sm text-gray-500">
                      {slot.start} - {slot.end}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 커스텀 시간 입력 */}
          {useCustomTime && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시작 시간
                </label>
                <input
                  type="time"
                  value={customStartTime}
                  onChange={(e) => setCustomStartTime(e.target.value)}
                  min="06:00"
                  max="23:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  종료 시간
                </label>
                <input
                  type="time"
                  value={customEndTime}
                  onChange={(e) => setCustomEndTime(e.target.value)}
                  min="06:00"
                  max="23:59"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* 충돌 검사 결과 */}
        {conflictCheck?.hasConflict && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{conflictCheck.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={loading || !selectedSeat || conflictCheck?.hasConflict}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? '예약 중...' : '예약하기'}
          </button>
        </div>
      </form>
    </div>
  );
};
